import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../ui/button';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { Plus, X } from 'lucide-react';
import { Input } from '../../ui/input';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../../ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { POST_TYPES } from '../../../lib/utils';

const AddPostModal = ({ isOpen, onClose, onSubmit, post = null, isEditMode = false }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(POST_TYPES.NEWS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const titleInputRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 640px)');

  useEffect(() => {
    if (isOpen && post && isEditMode) {
      setTitle(post.title || '');
      setDescription(post.description || '');
      setCategory(post.category || POST_TYPES.NEWS);
    } else if (isOpen && !isEditMode) {
      // Reset form for new post
      setTitle('');
      setDescription('');
      setCategory(POST_TYPES.NEWS);
    }

    // Focus the title input when modal opens
    if (isOpen && titleInputRef.current) {
      setTimeout(() => {
        titleInputRef.current.focus();
      }, 300);
    }
  }, [isOpen, post, isEditMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      
      const postData = {
        title: title.trim(),
        description: description.trim(),
        category
      };
      
      if (isEditMode && post) {
        postData.id = post.id;
      }
      
      await onSubmit(postData);
      onClose();
    
    } catch (error) {
      console.error('Error submitting post:', error);

    } finally {
      setIsSubmitting(false);
    }
  };

  const formContent = (id) => (
    <form id={id} onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="title" className="text-sm font-medium">
          {t('postForm.titleLabel')}
        </label>
        <Input
          id="title"
          ref={titleInputRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('postForm.titlePlaceholder')}
          required
        />
      </div>
      
      <div className="space-y-2">
        <label htmlFor="description" className="text-sm font-medium">
          {t('postForm.descLabel')}
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-neutral-100 dark:bg-neutral-800 
                   border border-transparent focus:border-primary focus:ring-2 
                   focus:ring-primary/20 transition-all min-h-[120px]"
          placeholder={t('postForm.descPlaceholder')}
          required
        />
      </div>
      
      <div className="space-y-2">
        <label htmlFor="category" className="text-sm font-medium">
          {t('postForm.categoryLabel')}
        </label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="bg-neutral-100 dark:bg-neutral-800">
            <SelectValue placeholder={t('postForm.categoryPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={POST_TYPES.NEWS}>{t('postTypes.news')}</SelectItem>
            <SelectItem value={POST_TYPES.EVENT}>{t('postTypes.event')}</SelectItem>
            <SelectItem value={POST_TYPES.LOST_FOUND}>{t('postTypes.lost_found')}</SelectItem>
            <SelectItem value={POST_TYPES.ANNOUNCEMENT}>{t('postTypes.announcement')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </form>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="max-h-[85vh] sm:max-h-[90vh]">
          <div className="flex flex-col h-[85vh] sm:h-auto">
            {/* Header */}
            <DrawerHeader className="border-b border-neutral-200 dark:border-neutral-700 pb-3">
              <div className="flex items-center justify-between">
                <DrawerTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Plus className="h-5 w-5 text-primary" />
                  <span>{isEditMode ? t('postForm.editTitle') : t('postForm.createTitle')}</span>
                </DrawerTitle>
                <DrawerClose className="rounded-full p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800">
                  <X className="h-4 w-4" />
                </DrawerClose>
              </div>
              <DrawerDescription className="mt-1">
                {isEditMode 
                  ? t('postForm.editDesc')
                  : t('postForm.createDesc')}
              </DrawerDescription>
            </DrawerHeader>

            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {formContent("post-form-mobile")}
            </div>

            {/* Footer with action buttons */}
            <DrawerFooter className="border-t border-neutral-200 dark:border-neutral-700 pt-3 pb-5 sm:pb-6">
              <Button
                type="submit"
                form="post-form-mobile"
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting 
                  ? (isEditMode ? t('postForm.saving') : t('postForm.creating'))
                  : (isEditMode ? t('postForm.saveChanges') : t('postForm.create'))}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline" className="w-full mt-2">
                  {t('common.cancel')}
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('postForm.editTitle') : t('postForm.createTitle')}</DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? t('postForm.editDesc')
              : t('postForm.createDesc')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="pt-2">
          {formContent("post-form-desktop")}
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0 mt-6">
          <Button variant="outline" type="button" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="post-form-desktop" disabled={isSubmitting}>
            {isSubmitting 
              ? (isEditMode ? t('postForm.saving') : t('postForm.creating'))
              : (isEditMode ? t('postForm.saveChanges') : t('postForm.create'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddPostModal;